import {useMemo} from 'react'

import {documentToPlainTextString} from '@contentful/rich-text-plain-text-renderer'
import {documentToReactComponents} from '@contentful/rich-text-react-renderer'
import {BLOCKS, INLINES} from '@contentful/rich-text-types'

import {Hero, Grid, BreakpointSize, useWindowSize, Text, InlineLink} from '@primer/react-brand'

import {useRoutePayload} from '@github-ui/react-core/use-route-payload'

import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'

import type {GenericContent, GenericSectionWithIds} from '../../../../brand/lib/types/contentful'

import {cohortFunnelBuilder} from '../../../../lib/analytics'
import {HeroVideo} from './HeroVideo'

type Props = {
  contentfulContent: GenericSectionWithIds
}

export function HeroSection(props: Props) {
  const {contentfulContent} = props

  const {featuresCopilotAiCodeEditorHero} = contentfulContent.ids as {
    featuresCopilotAiCodeEditorHero: GenericContent
  }

  const {heading, links, ctas, content, media} = featuresCopilotAiCodeEditorHero?.fields || {}

  // Extract video and poster from media (matching main copilot schema)
  const heroVideoLarge = media?.find(
    (item: unknown) =>
      item != null &&
      typeof item === 'object' &&
      'fields' in item &&
      item.fields != null &&
      typeof item.fields === 'object' &&
      'id' in item.fields &&
      item.fields.id === 'heroVideoLarge',
  )

  const heroVideoSmall = media?.find(
    (item: unknown) =>
      item != null &&
      typeof item === 'object' &&
      'fields' in item &&
      item.fields != null &&
      typeof item.fields === 'object' &&
      'id' in item.fields &&
      item.fields.id === 'heroVideoSmall',
  )

  const heroPosterLarge = media?.find(
    (item: unknown) =>
      item != null &&
      typeof item === 'object' &&
      'fields' in item &&
      item.fields != null &&
      typeof item.fields === 'object' &&
      'id' in item.fields &&
      item.fields.id === 'heroPosterLarge',
  )

  const heroPosterSmall = media?.find(
    (item: unknown) =>
      item != null &&
      typeof item === 'object' &&
      'fields' in item &&
      item.fields != null &&
      typeof item.fields === 'object' &&
      'id' in item.fields &&
      item.fields.id === 'heroPosterSmall',
  )

  const defaultCtas = ctas?.find(ctaGroup => ctaGroup.fields.condition === 'None')
  const primaryCta = defaultCtas?.fields.primary || links?.at(0)
  const secondaryCta = defaultCtas?.fields.secondary || links?.at(1)

  const tertiaryCta = content?.find(item => item.fields.id === 'featuresCopilotAiCodeEditorHeroTertiaryCta') as
    | GenericContent
    | undefined

  const {cft} = useRoutePayload<{cft: string}>()
  const withCft = cohortFunnelBuilder(cft)

  const windowSize = useWindowSize()
  const isDesktopView = useMemo(
    () =>
      [BreakpointSize.MEDIUM, BreakpointSize.LARGE, BreakpointSize.XLARGE, BreakpointSize.XXLARGE].includes(
        windowSize.currentBreakpointSize!,
      ),
    [windowSize.currentBreakpointSize],
  )

  return (
    <Grid as="section" className="position-relative">
      <Grid.Column span={12}>
        {featuresCopilotAiCodeEditorHero ? (
          <Hero align="center" className="pb-4">
            <Hero.Heading size="2" data-hpc>
              {heading}
            </Hero.Heading>

            <div className="lp-Hero-ctaButtons">
              {primaryCta && (
                <Hero.PrimaryAction
                  href={primaryCta.fields.href}
                  hasArrow={false}
                  {...getAnalyticsEvent({
                    action: primaryCta.fields.text,
                    tag: 'button',
                    context: 'CTAs',
                    location: 'hero',
                  })}
                >
                  {primaryCta.fields.text}
                </Hero.PrimaryAction>
              )}

              {secondaryCta ? (
                <Hero.SecondaryAction
                  href={withCft(secondaryCta.fields.href)}
                  hasArrow={false}
                  variant="subtle"
                  {...getAnalyticsEvent({
                    action: secondaryCta.fields.text,
                    tag: 'button',
                    context: 'CTAs',
                    location: 'hero',
                  })}
                >
                  {secondaryCta.fields.text}
                </Hero.SecondaryAction>
              ) : null}
            </div>

            {isDesktopView && tertiaryCta && tertiaryCta.fields.text ? (
              <Text weight="medium" style={{marginTop: '1rem'}}>
                {documentToReactComponents(tertiaryCta.fields.text, {
                  renderNode: {
                    [BLOCKS.PARAGRAPH]: (_, children) => [children],
                    [INLINES.HYPERLINK]: (node, children) => (
                      <InlineLink
                        href={node.data.uri}
                        className="tertiary-cta"
                        {...getAnalyticsEvent({
                          action: documentToPlainTextString(node, ' '),
                          tag: 'link',
                          context: 'CTAs',
                          location: 'hero',
                        })}
                      >
                        {children}
                      </InlineLink>
                    ),
                  },
                })}
              </Text>
            ) : null}
          </Hero>
        ) : null}

        <div className="mt-6 mt-md-8">
          {heroVideoLarge ? (
            <HeroVideo
              description={heroVideoLarge.fields.description}
              posterSrcLarge={heroPosterLarge?.fields.asset?.fields.file.url}
              posterSrcSmall={heroPosterSmall?.fields.asset?.fields.file.url}
              videoSrcLarge={heroVideoLarge.fields.asset.fields.file.url}
              videoSrcSmall={heroVideoSmall?.fields.asset?.fields.file.url}
            />
          ) : null}
        </div>
      </Grid.Column>
    </Grid>
  )
}

try{ HeroSection.displayName ||= 'HeroSection' } catch {}